import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';

/**
 * A day block's clock (spec "Blocks", owner Q4): the **Project time zone** unless the builder switches
 * this block to UTC. 'project' generates EXACTLY the code it always did — no argument — so a block left
 * alone and a block saved before this existed compile to the same text. Only 'utc' adds `'utc'`.
 * Either way the block's value is a UTC instant (`…Z`); the choice only decides whose days it counts.
 * ⚠ The value must never contain `$` or `%`: resolveFunctionCode rewrites both (see time_difference).
 */
export const ZONE_FIELD = 'ZONE';
export const zoneDropdown = () => new Blockly.FieldDropdown([['in project time', 'project'], ['in UTC', 'utc']]);
export const zoneArg = (block: any): string => (block.getFieldValue(ZONE_FIELD) === 'utc' ? ", 'utc'" : '');

export const TimeStampFns = {
  customBlockForCurrentDateISO() {
  Blockly.Blocks['current_date_iso'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('current date ISO');
      this.setOutput(true, 'TimeStamp');
      this.setColour('#F5A623');
      this.setTooltip('Returns the current date and time.');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['current_date_iso'] = function (
    block: any
  ) {
    // now() (2026-09-19): the same UTC instant, through a helper the runtime owns. The inline Date it replaced
    // is below, per the no-delete rule; functions saved before this keep it and produce the identical value.
    // const code = `new Date().toISOString()`;
    // Was (2026-09-19): const code = `this.now()`;
    // ⚠ A pinned project publishes onto the engine it recorded, and an engine older than now() throws
    // `this.now is not a function`. Guard it: the same instant on the new engine, the old inline Date on an old one.
    const code = `(this.now ? this.now() : new Date().toISOString())`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}
    ,
      customBlockForSetDate() {
    Blockly.Blocks['set_date'] = {
      init: function () {
        // Absolute date from inline, range-clamped numbers → 00:00 UTC. Literal only (no sockets).
        this.appendDummyInput()
          .appendField('date  day')
          .appendField(new Blockly.FieldNumber(1, 1, 31, 1), 'DAY')
          .appendField('month')
          .appendField(new Blockly.FieldNumber(1, 1, 12, 1), 'MONTH')
          .appendField('year')
          .appendField(new Blockly.FieldNumber(2026, 1970, 9999, 1), 'YEAR')
          .appendField(zoneDropdown(), ZONE_FIELD);
        this.setInputsInline(true);
        this.setOutput(true, 'TimeStamp');
        this.setColour('#F5A623');
        this.setTooltip('A specific date at 00:00 in the project time zone (or UTC, if switched). An impossible day clamps to the month end.');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['set_date'] = function (block: any) {
      const day = block.getFieldValue('DAY');
      const month = block.getFieldValue('MONTH');
      const year = block.getFieldValue('YEAR');
      const code = `this.buildDate(${day}, ${month}, ${year}${zoneArg(block)})`;
      return [code, (javascriptGenerator as any).ORDER_ATOMIC];
    };
  }
    ,
      customBlockForSetTime() {
    Blockly.Blocks['set_time'] = {
      init: function () {
        // Set hour/minute on any incoming date (seconds zeroed, UTC). Numbers are inline, range-clamped.
        this.appendValueInput('DATE')
          .setCheck('TimeStamp')
          .appendField('set time on date');
        this.appendDummyInput()
          .appendField('hour')
          .appendField(new Blockly.FieldNumber(0, 0, 23, 1), 'HOUR')
          .appendField('minute')
          .appendField(new Blockly.FieldNumber(0, 0, 59, 1), 'MINUTE');
        this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
        this.setInputsInline(true);
        this.setOutput(true, 'TimeStamp');
        this.setColour('#F5A623');
        this.setTooltip('Set the hour and minute on a date, on that date\'s own day in the project time zone (or UTC, if switched). Seconds are always zero.');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['set_time'] = function (block: any) {
      const date = (javascriptGenerator as any).valueToCode(block, 'DATE', (javascriptGenerator as any).ORDER_ATOMIC) || "''";
      const hour = block.getFieldValue('HOUR');
      const minute = block.getFieldValue('MINUTE');
      const code = `this.setTimeOfDay(${date}, ${hour}, ${minute}${zoneArg(block)})`;
      return [code, (javascriptGenerator as any).ORDER_ATOMIC];
    };
  }
    ,
      customBlockForDateAddDays() {
    Blockly.Blocks['date_add_days'] = {
      init: function () {
        // Inputs are matched by NAME on load, so old saved blocks (DATE + DAYS, no UNIT) still connect;
        // the UNIT dropdown defaults to 'day', so they behave exactly as before.
        this.appendValueInput("DAYS")
            .setCheck("Number")
            .appendField("add");
        this.appendDummyInput()
            .appendField(new Blockly.FieldDropdown([
              ['days', 'day'],
              ['months', 'month'],
              ['years', 'year'],
              ['hours', 'hour'],
              ['minutes', 'minute'],
            ]), 'UNIT');
        this.appendValueInput("DATE")
            .setCheck("TimeStamp")
            .appendField("to date");
        this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
        this.setInputsInline(true);
        this.setOutput(true, "TimeStamp");
        this.setColour('#F5A623');
        this.setTooltip("Add or subtract days, months, years, hours or minutes from a date (use a negative number to subtract). Months/years clamp to the month end — Jan 31 + 1 month = Feb 28. No seconds.");
        this.setHelpUrl("");
      }
    };

    (javascriptGenerator as any).forBlock['date_add_days'] = function (block: any) {
      const date = javascriptGenerator.valueToCode(block, 'DATE', 0) || "''";
      const days = javascriptGenerator.valueToCode(block, 'DAYS', 0) || "0";
      const unit = block.getFieldValue('UNIT') || 'day';
      const code = `this.dateAddDays(${date}, ${days}, '${unit}'${zoneArg(block)})`;
      return [code, (javascriptGenerator as any).ORDER_ATOMIC];
    };
  }
    ,
      customBlockForDateDifference() {
        const blockColor = '#F5A623';
        const buildBlock = (
          type: string,
          heading: string,
          accessor: 'hours' | 'minutes'
        ) => {
          Blockly.Blocks[type] = {
            init: function () {
              this.appendDummyInput().appendField(heading);
              this.appendValueInput('DATE_A')
                .setCheck('TimeStamp')
                .appendField('date A');
              this.appendValueInput('DATE_B')
                .setCheck('TimeStamp')
                .appendField('date B');
              this.setInputsInline(false);
              this.setOutput(true, 'Number');
              this.setColour(blockColor);
              this.setTooltip('Difference between two dates (automatic format detection).');
              this.setHelpUrl('');
            },
          };
    
          (javascriptGenerator as any).forBlock[type] = function (block: any) {
            const dateA =
              (javascriptGenerator as any).valueToCode(
                block,
                'DATE_A',
                (javascriptGenerator as any).ORDER_NONE
              ) || "''";
            const dateB =
              (javascriptGenerator as any).valueToCode(
                block,
                'DATE_B',
                (javascriptGenerator as any).ORDER_NONE
              ) || "''";
    
            const code = `this.getDateDifference(${dateA}, ${dateB}).${accessor}`;
            return [code, (javascriptGenerator as any).ORDER_ATOMIC];
          };
        };
    
        buildBlock('date_diff_hours', 'date difference (hours)', 'hours');
        buildBlock('date_diff_minutes', 'date difference (minutes)', 'minutes');
      },

      customBlockForStartOfMonth() {
        Blockly.Blocks['date_start_of_month'] = {
          init: function () {
            this.appendValueInput('DATE').setCheck('TimeStamp').appendField('first day of month');
            this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
            this.setOutput(true, 'TimeStamp');
            this.setColour('#F5A623');
            this.setTooltip('Given a date, returns the FIRST day of that month (00:00) as a TimeStamp.');
            this.setHelpUrl('');
          },
        };
        (javascriptGenerator as any).forBlock['date_start_of_month'] = function (block: any) {
          const date = (javascriptGenerator as any).valueToCode(block, 'DATE', (javascriptGenerator as any).ORDER_NONE) || "''";
          return [`this.startOfMonth(${date}${zoneArg(block)})`, (javascriptGenerator as any).ORDER_ATOMIC];
        };
      },

      customBlockForEndOfMonth() {
        Blockly.Blocks['date_end_of_month'] = {
          init: function () {
            this.appendValueInput('DATE').setCheck('TimeStamp').appendField('last day of month');
            this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
            this.setOutput(true, 'TimeStamp');
            this.setColour('#F5A623');
            this.setTooltip('Given a date, returns the LAST day of that month (23:59:59.999) as a TimeStamp — leap years handled.');
            this.setHelpUrl('');
          },
        };
        (javascriptGenerator as any).forBlock['date_end_of_month'] = function (block: any) {
          const date = (javascriptGenerator as any).valueToCode(block, 'DATE', (javascriptGenerator as any).ORDER_NONE) || "''";
          return [`this.endOfMonth(${date}${zoneArg(block)})`, (javascriptGenerator as any).ORDER_ATOMIC];
        };
      },

      customBlockForStartOfDay() {
        Blockly.Blocks['date_start_of_day'] = {
          init: function () {
            this.appendValueInput('DATE').setCheck('TimeStamp').appendField('start of day');
            this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
            this.setOutput(true, 'TimeStamp');
            this.setColour('#F5A623');
            this.setTooltip('Given a date, returns the START of that day (00:00) as a TimeStamp.');
            this.setHelpUrl('');
          },
        };
        (javascriptGenerator as any).forBlock['date_start_of_day'] = function (block: any) {
          const date = (javascriptGenerator as any).valueToCode(block, 'DATE', (javascriptGenerator as any).ORDER_NONE) || "''";
          return [`this.startOfDay(${date}${zoneArg(block)})`, (javascriptGenerator as any).ORDER_ATOMIC];
        };
      },

      customBlockForEndOfDay() {
        Blockly.Blocks['date_end_of_day'] = {
          init: function () {
            this.appendValueInput('DATE').setCheck('TimeStamp').appendField('end of day');
            this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
            this.setOutput(true, 'TimeStamp');
            this.setColour('#F5A623');
            this.setTooltip('Given a date, returns the END of that day (23:59:59.999) as a TimeStamp — use it for a "to" date.');
            this.setHelpUrl('');
          },
        };
        (javascriptGenerator as any).forBlock['date_end_of_day'] = function (block: any) {
          const date = (javascriptGenerator as any).valueToCode(block, 'DATE', (javascriptGenerator as any).ORDER_NONE) || "''";
          return [`this.endOfDay(${date}${zoneArg(block)})`, (javascriptGenerator as any).ORDER_ATOMIC];
        };
      },

      /**
       * time_difference — ONE statement block that writes THREE variables. dateA/dateB are read; the
       * isPositive/whole/remainder slots hold get_variable children that the generator ASSIGNS into
       * (same shape as the *_api_functions / *_ap_workflow call blocks — see api-function-blocks.ts).
       * Numbers are unsigned; direction lives on the Boolean. Replaces date_diff_hours/minutes.
       */
      customBlockForTimeDifference() {
        Blockly.Blocks['time_difference'] = {
          init: function () {
            this.appendValueInput('dateA').setCheck('TimeStamp').appendField('time difference from');
            this.appendValueInput('dateB').setCheck('TimeStamp').appendField('to');
            this.appendDummyInput().appendField('in').appendField(
              new Blockly.FieldDropdown([
                ['Years', 'year'],
                ['Months', 'month'],
                ['Weeks', 'week'],
                ['Days', 'day'],
                ['Hours', 'hour'],
                ['Minutes', 'minute'],
              ]),
              'UNIT'
            ).appendField(zoneDropdown(), ZONE_FIELD);
            this.appendValueInput('isPositive').setCheck('Boolean').appendField('positive? →');
            this.appendValueInput('whole').setCheck('Number').appendField('whole →');
            this.appendValueInput('remainder').setCheck('Number').appendField('remainder →');
            this.setInputsInline(false);
            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour('#F5A623');
            this.setTooltip('Difference between two timestamps in the chosen unit. Writes a Boolean (dateB at/after dateA), a whole count, and the leftover in the next-smaller unit. Numbers are always positive.');
            this.setHelpUrl('');
          },
        };

        (javascriptGenerator as any).forBlock['time_difference'] = function (block: any) {
          const g = javascriptGenerator as any;
          const dateA = g.valueToCode(block, 'dateA', g.ORDER_NONE) || "''";
          const dateB = g.valueToCode(block, 'dateB', g.ORDER_NONE) || "''";
          const unit = block.getFieldValue('UNIT') || 'day';
          // NOTE: must NOT keep '$' (or '%') — resolveFunctionCode() rewrites those as variable-path
          // markers (localVariable$… → …localVariables., then a blanket $→. and %→.), which would turn
          // this temp's name into a dotted member expression and make `const <name>` a syntax error.
          const tmp = '__td_' + String(block.id).replace(/[^a-zA-Z0-9_]/g, '_');
          const posRef = g.valueToCode(block, 'isPositive', g.ORDER_ASSIGNMENT);
          const wholeRef = g.valueToCode(block, 'whole', g.ORDER_ASSIGNMENT);
          const remRef = g.valueToCode(block, 'remainder', g.ORDER_ASSIGNMENT);
          let code = `const ${tmp} = this.timeDifference(${dateA}, ${dateB}, '${unit}'${zoneArg(block)});\n`;
          if (posRef && !posRef.includes('NAN')) code += `${posRef} = ${tmp}.isPositive;\n`;
          if (wholeRef && !wholeRef.includes('NAN')) code += `${wholeRef} = ${tmp}.whole;\n`;
          if (remRef && !remRef.includes('NAN')) code += `${remRef} = ${tmp}.remainder;\n`;
          return code;
        };
      }

};